import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UtilityService } from 'src/app/services/utility.service';
import { UserService } from 'src/app/services/user.service';
import { RegistrationService } from '../../services/registration.service';
import { ContactService } from '../../services/contact.service';
import { CompanyService } from '../../services/company.service';

import { SecurityQuestionsListItem, UserInformation, VerifyUserRegistration } from '../../models/registration';
import { SecurityQuestionChange } from '../../models/contact';
import { CompanyInformation } from '../../models/company';

@Component({
  selector: 'app-user-security-question',
  templateUrl: './user-security-question.component.html',
  styleUrls: ['./user-security-question.component.scss']
})
export class UserSecurityQuestionComponent implements OnInit {
  private _handleError: HandleError;
  private _logging = environment.logging;
  fieldValidations = environment.fieldValidations;
  recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;
  securityQuestionChangeFound = false;

  securityQuestionsList: SecurityQuestionsListItem[] = [];
  securityQuestionsListFound = false;

  userSecurityQuestionForm: UntypedFormGroup;
  userNameFormControl = new UntypedFormControl();
  securityQuestionFormControl = new UntypedFormControl();
  securityQuestionAnswerFormControl = new UntypedFormControl();
  recaptchaFormControl = new UntypedFormControl();

  recaptchaValid = false;

  showSecurityQuestionSpinner = false;
  showSecurityQuestionError = false;

  companyInformation: CompanyInformation;
  securityQuestionChange: SecurityQuestionChange;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _utilityService: UtilityService,
    private _userService: UserService,
    private _registrationService: RegistrationService,
    private _contactService: ContactService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('UserSecurityQuestionComponent');
    this.getData();
    this.scrollToTop();
  }

  ngOnInit() {}

  getData(): void {
    const companyCode: string = this._companyService.getCompanyCode();

    this.companyInformation = this._companyService.getNewCompanyInformation();

    this.securityQuestionChange = this._contactService.getNewSecurityQuestionChange();

    this._companyService.companyInformation.subscribe(
      response => (this.companyInformation = response),
      error => this._handleError('companyInformation.subscribe', error)
    );

    this.getSecurityQuestionData();
    this.getSecurityQuestionsList();
  }

  getSecurityQuestionData(): void {
    this.showSpinner = true;
    this.securityQuestionChangeFound = false;

    this.securityQuestionChange.userId = this._userService.getUserId();
    this.securityQuestionChange.userName = this._userService.getUserName();

    this._contactService.getSecurityQuestionData(this.securityQuestionChange).subscribe(
      result => {
        this.securityQuestionChangeFound = true;
        this.securityQuestionChange.securityQuestionId = result.securityQuestionId;
        this.securityQuestionChange.securityQuestionText = result.securityQuestionText;
        this.securityQuestionChange.securityQuestionAnswer = result.securityQuestionAnswer;

        if (this._logging.serviceCallTraceLog) {
          console.log('UserSecurityQuestionComponent.getSecurityQuestionData :: result.userId = ' + result.userId);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('UserSecurityQuestionComponent.getSecurityQuestionData :: result = ' + JSON.stringify(result));
        }
        this.getDataComplete();
      },
      error => this._handleError('getContactInformationData', error)
    );
  }

  getSecurityQuestionsList(): void {
    this.showSpinner = true;
    this.securityQuestionsListFound = false;
    this._registrationService.getSecurityQuestionsList().subscribe(
      resultArray => {
        this.securityQuestionsListFound = resultArray !== null && resultArray.length > 0;
        this.securityQuestionsList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('UserSecurityQuestionComponent.getSecurityQuestionsList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('UserSecurityQuestionComponent.getSecurityQuestionsList :: result = ' + JSON.stringify(resultArray));
        }
        this.getDataComplete();
      },
      error => this._handleError('getSecurityQuestionsList', error)
    );
  }

  getDataComplete(): void {
    if (this.securityQuestionChangeFound && this.securityQuestionsListFound) {
      this.showSpinner = false;
      this.createForm();
    }
  }

  createForm(): void {
    this.userSecurityQuestionForm = this._formBuilder.group({});
    this.userSecurityQuestionForm.addControl('userNameFormControl', this.userNameFormControl);
    this.userSecurityQuestionForm.addControl('securityQuestionFormControl', this.securityQuestionFormControl);
    this.userSecurityQuestionForm.addControl('securityQuestionAnswerFormControl', this.securityQuestionAnswerFormControl);
    this.userSecurityQuestionForm.addControl('recaptchaFormControl', this.recaptchaFormControl);

    this.setFormValues();
    this.setFormValidations(false);
  }

  setFormValues(): void {
    this.userSecurityQuestionForm.setValue({
      userNameFormControl: this.securityQuestionChange.userName,
      securityQuestionFormControl:
        this.securityQuestionChange.securityQuestionId !== null ? this.securityQuestionChange.securityQuestionId.toString() : '',
      securityQuestionAnswerFormControl: this.securityQuestionChange.securityQuestionAnswer,
      recaptchaFormControl: null
    });
  }

  setFormValidations(clearAllValidations: boolean) {
    if (clearAllValidations) {
      this.userSecurityQuestionForm.get('userNameFormControl').setValidators([]);
      this.userSecurityQuestionForm.get('securityQuestionFormControl').setValidators([]);
      this.userSecurityQuestionForm.get('securityQuestionAnswerFormControl').setValidators([]);
      this.userSecurityQuestionForm.get('recaptchaFormControl').setValidators([]);
    } else {
      this.userSecurityQuestionForm.get('userNameFormControl').setValidators([Validators.required]);
      this.userSecurityQuestionForm.get('securityQuestionFormControl').setValidators([Validators.required]);
      this.userSecurityQuestionForm.get('securityQuestionAnswerFormControl').setValidators([Validators.required]);
      this.userSecurityQuestionForm.get('recaptchaFormControl').setValidators([Validators.required]);
    }

    this.userSecurityQuestionForm.get('userNameFormControl').markAsPristine();
    this.userSecurityQuestionForm.get('recaptchaFormControl').markAsPristine();
    this.userSecurityQuestionForm.get('securityQuestionFormControl').markAsPristine();
    this.userSecurityQuestionForm.get('securityQuestionAnswerFormControl').markAsPristine();

    this.userSecurityQuestionForm.get('userNameFormControl').markAsUntouched();
    this.userSecurityQuestionForm.get('recaptchaFormControl').markAsUntouched();
    this.userSecurityQuestionForm.get('securityQuestionFormControl').markAsUntouched();
    this.userSecurityQuestionForm.get('securityQuestionAnswerFormControl').markAsUntouched();

    this.userSecurityQuestionForm.get('userNameFormControl').updateValueAndValidity();
    this.userSecurityQuestionForm.get('recaptchaFormControl').updateValueAndValidity();
    this.userSecurityQuestionForm.get('securityQuestionFormControl').updateValueAndValidity();
    this.userSecurityQuestionForm.get('securityQuestionAnswerFormControl').updateValueAndValidity();
  }

  submitEnabled(): boolean {
    return this.userSecurityQuestionForm.valid;
  }

  submitSecurityQuestion(): void {
    if (this.userSecurityQuestionForm.valid) {
      this.showSecurityQuestionSpinner = true;

      this.securityQuestionChange.securityQuestionId = Number(this.userSecurityQuestionForm.get('securityQuestionFormControl').value);
      const selectedSecurityQuestionsList = this.securityQuestionsList.filter(
        x => x.securityQuestionId === Number(this.userSecurityQuestionForm.get('securityQuestionFormControl').value)
      );
      this.securityQuestionChange.securityQuestionText =
        selectedSecurityQuestionsList !== null && selectedSecurityQuestionsList.length > 0
          ? selectedSecurityQuestionsList[0].securityQuestion
          : null;
      this.securityQuestionChange.securityQuestionAnswer = this.userSecurityQuestionForm.get('securityQuestionAnswerFormControl').value;

      this._contactService.updateSecurityQuestion(this.securityQuestionChange).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('UserSecurityQuestionComponent.submitUser :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('UserSecurityQuestionComponent.submitUser :: result = ' + result);
          }

          this.showSecurityQuestionSpinner = false;

          if (Number(result) > 0) {
            this.showSecurityQuestionError = false;
            this._userService.setSecurityQuestionSetupFlag(true);
            this.openHome();
          } else {
            this.showSecurityQuestionError = true;
          }
        },
        error => {
          this._handleError('submitUser', error, this.securityQuestionChange);
          this.showSecurityQuestionSpinner = false;
          this.showSecurityQuestionError = true;
        }
      );
    } else {
      this.showSecurityQuestionSpinner = false;
      this.showSecurityQuestionError = true;
    }
  }

  getToolTip(element: string): string {
    let toolTip: string;
    switch (element.toUpperCase().trim()) {
      case 'SECURITYQUESTION': {
        toolTip =
          // tslint:disable-next-line:max-line-length quotemark
          "For your security and convenience, we are asking you to provide a security question (and corresponding answer), so that you may gain access to your account in the event you forget your User Name, Password, or both.  Security questions and answers are each limited to 40 characters in length.  We recommend choosing a question and answer that will be easy for you to remember, but hard for others to guess.  Some examples: \n\n Q: What is your favorite color? \n Q: What is your favorite restaurant? \n Q: What is your dog's name? \n\n The questions and answers are not restricted in any way, but we recommend you choose something that you will remember, and that won't change very often. (We will provide a tool to change them, if you so desire, however.)  We don't recommend choosing questions and answers that would be easy for others to obtain.  For example: \n\n Q: What is your address? \n Q: What is your Mother's maiden name? \n Q: What is your phone number?";
        break;
      }
      default: {
        toolTip = 'No ToolTip message has been defined for ' + element;
        break;
      }
    }
    return toolTip;
  }

  openHome(): void {
    if (this._logging.navigationTraceLog) {
      console.log('UserSecurityQuestionComponent.openHome');
    }
    this._router.navigate(['']);
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }
}
